/**
 * `SecureStore` over IndexedDB.
 *
 * "Secure" is the interface's word, not a claim about the browser: IndexedDB
 * is exactly as exposed to XSS as localStorage. It is what the web can offer,
 * and it is the store the rest of the offline stack already uses. The reason
 * that is acceptable here is upstream of this file — the access token
 * deliberately never persists (see lib/auth/tokens.ts), so what lands is a
 * refresh token the server can revoke. On a phone the same interface is backed
 * by the Keychain and the Android Keystore, where the name is literal.
 *
 * Self-contained on purpose. `lib/auth/tokens.ts` has its own IndexedDB code
 * today and moves to a shared package in the next phase; pointing this adapter
 * at it now would couple the platform layer to a module that is about to
 * leave, and in the wrong direction.
 */
import type { SecureStore } from '@kapture/platform';

const DB_NAME = 'kapture-secure';
const DB_VERSION = 1;
const STORE = 'values';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = work(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

export const idbSecureStore: SecureStore = {
  async get(key: string): Promise<string | null> {
    const value = await withStore<string | undefined>('readonly', (store) => store.get(key));
    return value ?? null;
  },
  async set(key: string, value: string): Promise<void> {
    await withStore('readwrite', (store) => store.put(value, key));
  },
  async delete(key: string): Promise<void> {
    await withStore('readwrite', (store) => store.delete(key));
  },
};
